import { hideLoader } from '../../../modules/loaders';
import { checkoutFailed } from './messages';

/**
 * Paying, which on this door is how the account gets made.
 *
 * The paid plans never post the registration form. The fields are collected here
 * and sent to /create-checkout-session, Stripe takes the card, and the account is
 * created on the way back. That is why this door has no confirmation step and no
 * pending state: nothing exists until the payment clears.
 *
 * Stripe is loaded by the page, not bundled. When the key is missing or the script
 * failed, the paid plans are hidden rather than offered and then refused at the
 * last step.
 */

let stripe = null;

/**
 * Sets the paid plans up, and says whether they can be offered at all.
 *
 * @returns {boolean} false when Stripe is unusable, and the plans stay hidden.
 */
export function openCheckoutWith(publishableKey) {
    if (! publishableKey) {
        console.warn('Stripe key is not set. Skipping Stripe initialization.');

        return false;
    }

    try {
        stripe = Stripe(publishableKey);

        return true;
    } catch (error) {
        console.warn('Stripe initialization failed:', error);

        return false;
    }
}

/**
 * The profile fields this form draws, by the list the form itself declares.
 *
 * createCheckoutSession validates them with RegistrationSpec, so what the spec
 * requires is exactly what has to be sent: reading the list from the form keeps the
 * two the same, where fields written out here would miss the next one it adds.
 */
function profileBlock() {
    const block = document.querySelector('[data-registration-rules]');

    if (! block) {
        return {};
    }

    const fields = Object.keys(JSON.parse(block.dataset.registrationRules || '{}'));

    return Object.fromEntries(
        fields.map((name) => [name, block.querySelector(`[name="${name}"]`)?.value || ''])
    );
}

// A session being created. A second click would open a second one for the same
// account while the first redirect is on its way.
let starting = false;

/** Everything the session needs, read from the form the reader just filled in. */
export function startPayment({ subscriptionType, bookSlug, resume = false }) {
    if (starting) {
        return;
    }

    if (! stripe) {
        hideLoader();
        checkoutFailed();

        return;
    }

    const value = (id) => document.getElementById(id)?.value || '';

    const name = value('name');
    const email = value('email');
    const password = value('password');

    // Nothing is sent half-filled: the validator has run, but a plan can be chosen
    // and the form left alone, and an empty session is a charge nobody can explain.
    // A resumed purchase sends none of it: the account is in the session already.
    if (! resume && (! name || ! email || ! password)) {
        hideLoader();

        return;
    }

    starting = true;

    const order = resume
        ? { resume: true, subscription_type: subscriptionType, book_slug: bookSlug, discount_code: value('appliedDiscountCode') }
        : {
            subscription_type: subscriptionType,
            book_slug: bookSlug,
            name,
            email,
            password,
            discount_code: value('appliedDiscountCode'),
            ...profileBlock()
        };

    fetch('/create-checkout-session', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify(order)
    })
        .then((response) => response.json())
        .then((data) => {
            if (! data.checkoutSessionId) {
                throw new Error('Error creating payment session.');
            }

            return stripe.redirectToCheckout({ sessionId: data.checkoutSessionId });
        })
        .then((result) => {
            // A redirect Stripe could not make resolves with the error rather than throwing.
            if (result && result.error) {
                throw result.error;
            }
        })
        .catch(() => {
            starting = false;
            hideLoader();
            checkoutFailed();
        });
}
